import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, KeyRound, CheckCircle2 } from 'lucide-react';
import { userLogin, userSignup, forgotPassword, resetPassword } from '../api/userAuth';
import { useUser } from '../context/UserContext';
import SEO from '../components/SEO';

type AuthMode = 'login' | 'signup' | 'forgot' | 'reset';

const LoginPage: React.FC = () => {
  const { login, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const resetToken = new URLSearchParams(location.search).get('token') || '';
  const [authMode, setAuthMode] = useState<AuthMode>(
    location.pathname === '/reset-password' && resetToken ? 'reset' : 'login'
  );
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    newPassword: '',
  });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error || successMsg) {
      requestAnimationFrame(() => {
        messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        messageRef.current?.focus({ preventScroll: true });
      });
    }
  }, [error, successMsg]);

  useEffect(() => {
    if (resetCooldown <= 0) return;

    const timer = window.setTimeout(() => {
      setResetCooldown(current => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resetCooldown]);

  // If already logged in, redirect to home
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const resetMessages = () => {
    setError('');
    setSuccessMsg('');
  };

  const handleLoginOrSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (authMode === 'login') {
        const res = await userLogin({ email: formData.email, password: formData.password });
        login(res.token, res.user);
        navigate('/');
      } else if (authMode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return;
        }

        const res = await userSignup({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        });
        login(res.token, res.user);
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const res = await forgotPassword(formData.email);
      setSuccessMsg(res.message || 'A password reset link has been sent to your email.');
      setResetCooldown(60);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const res = await resetPassword({
        token: resetToken,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      setSuccessMsg(res.message || 'Password reset successfully! Please sign in.');
      setFormData({ ...formData, password: '', newPassword: '' });
      setAuthMode('login');
      navigate('/login', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <SEO 
        title={
          authMode === 'login'
            ? 'Sign In | c nine'
            : authMode === 'signup'
            ? 'Create Account | c nine'
            : 'Reset Password | c nine'
        } 
        description="Login or create an account to shop premium tech accessories."
      />
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        {/* Top Icon for Forgot/Reset Password */}
        {(authMode === 'forgot' || authMode === 'reset') && (
          <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-indigo-100">
            <KeyRound className="w-6 h-6 text-indigo-600" />
          </div>
        )}

        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900">
            {authMode === 'login' && 'Welcome Back'}
            {authMode === 'signup' && 'Create Account'}
            {authMode === 'forgot' && 'Forgot Password?'}
            {authMode === 'reset' && 'Reset Password'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {authMode === 'login' && 'Please sign in to your account'}
            {authMode === 'signup' && 'Join us for a premium shopping experience'}
            {authMode === 'forgot' && 'Enter your email to receive a password reset link'}
            {authMode === 'reset' && 'Choose a new password for your Cnine account'}
          </p>
        </div>

        {error && (
          <div ref={messageRef} tabIndex={-1} className="bg-red-50 text-red-600 p-4 rounded-lg text-sm mb-6 border border-red-100 font-medium outline-none">
            {error}
          </div>
        )}

        {successMsg && (
          <div ref={messageRef} tabIndex={-1} className="bg-green-50 text-green-700 p-4 rounded-lg text-sm mb-6 border border-green-100 font-medium flex items-center outline-none">
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-600 flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Login or Signup Form */}
        {(authMode === 'login' || authMode === 'signup') && (
          <form onSubmit={handleLoginOrSignup} className="space-y-5">
            {authMode === 'signup' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                {authMode === 'login' && (
                  <button
                    type="button"
                    onClick={() => {
                      resetMessages();
                      setAuthMode('forgot');
                    }}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-4 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {authMode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.confirmPassword}
                  onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || resetCooldown > 0}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 font-medium text-lg mt-6 shadow-md"
              style={{ backgroundColor: '#4554a4' }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#374a8a';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#4554a4';
              }}
            >
              {loading ? 'Please wait...' : (authMode === 'login' ? 'Sign In' : 'Sign Up')}
            </button>
          </form>
        )}

        {/* Forgot Password Step 1 Form */}
        {authMode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registered Email Address</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 text-white rounded-lg transition-colors disabled:opacity-50 font-medium text-base mt-4 shadow-md flex items-center justify-center"
              style={{ backgroundColor: '#4554a4' }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#374a8a';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#4554a4';
              }}
            >
              {loading
                ? 'Sending Link...'
                : resetCooldown > 0
                ? `Resend available in ${resetCooldown}s`
                : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => {
                resetMessages();
                setAuthMode('login');
              }}
              className="w-full flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-900 pt-2 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Sign In
            </button>
          </form>
        )}

        {/* Reset Password Form */}
        {authMode === 'reset' && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={formData.newPassword}
                  onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                  placeholder="At least 6 characters"
                  className="w-full pl-4 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.confirmPassword}
                onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Enter your new password again"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !resetToken}
              className="w-full py-3 px-4 text-white rounded-lg transition-colors disabled:opacity-50 font-medium text-base mt-4 shadow-md flex items-center justify-center"
              style={{ backgroundColor: '#4554a4' }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#374a8a';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#4554a4';
              }}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>

            <button
              type="button"
              onClick={() => {
                resetMessages();
                setAuthMode('login');
              }}
              className="w-full flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-900 pt-2 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Sign In
            </button>
          </form>
        )}

        {/* Footer switch between Login & Signup */}
        {(authMode === 'login' || authMode === 'signup') && (
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
            {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => {
                resetMessages();
                setAuthMode(authMode === 'login' ? 'signup' : 'login');
              }}
              className="font-semibold transition-colors focus:outline-none"
              style={{ color: '#4554a4' }}
            >
              {authMode === 'login' ? 'Create one now' : 'Sign In instead'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
